import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { monetate } from '@grandvisionhq/svc-monetate';
import AppointmentConfirmationContainer from '../../../../components/AppointmentConfirmation/AppointmentConfirmationContainer.js';
import { bookAppointmentConfirmationScreenEvent } from '../../../../data-layer/actions.js';
import { useAppointmentContext } from '../../context/AppointmentContext.js';
import { useRoutingContext } from '../../context/RoutingContext/context.js';
import { getBookAppointmentStore } from '../../data/book-appointment-store-by-id.js';
import { getBookAppointmentTryInStoreProducts } from '../../data/book-appointment-try-in-store-products.js';
import { getBookAppointmentType } from '../../data/book-appointment-type.js';
import { MissingStoreIdError, AlternativeUrlStoreError, MissingTypeIdError, MissingTimeSlotStartError, MissingTimeSlotEndError, MissingAppointmentIdError, InvalidVirtualAppointmentError, StoreNotFoundError, TypeNotFoundError, TypeNotAvailableForStoreError } from '../../errors.js';
import '@grandvisionhq/graphql';
import '@grandvisionhq/state';
import '@grandvisionhq/utils';
import '../../queries/get-book-appointment-store.js';
import '../../queries/get-book-appointment-store-by-id.js';
import '../../queries/get-book-appointment-type.js';
import useVirtualAppointment from '../../hooks/use-virtual-appointment.js';
import { getFormattedParams, hasValidVirtualAppointmentParams, generateVirtualAppointmentStoreByIdData, hasInvalidVirtualAppointmentParams, isAppointmentTypeValid } from '../../utils.js';

const whereStep = 'where';
const isClient = typeof window !== 'undefined';
const ConfirmationRoot = ()=>{
    const [, { store , type , timeSlot , appointment  }] = useAppointmentContext();
    const [{ isVirtualAppointmentType  }] = useVirtualAppointment();
    const [{ goTo  }] = useRoutingContext();
    React.useEffect(()=>{
        if (store && type && timeSlot && appointment) {
            const dataLayerEvent = bookAppointmentConfirmationScreenEvent({
                store: !isVirtualAppointmentType(type) ? {
                    code: store.code,
                    globalStoreId: store.globalStoreId,
                    streetName: store.streetName,
                    town: store.town,
                    name: store.name,
                    postalCode: store.postalCode,
                    streetNumber: store.streetNumber,
                    additionalStreetInfo: store.additionalStreetInfo
                } : undefined,
                appointment: {
                    id: appointment === null || appointment === void 0 ? void 0 : appointment.id,
                    appointmentType: {
                        id: type.id,
                        isVirtualAppointment: isVirtualAppointmentType(type),
                        ageGroup: type.ageGroup
                    },
                    startTime: timeSlot === null || timeSlot === void 0 ? void 0 : timeSlot.from,
                    endTime: timeSlot === null || timeSlot === void 0 ? void 0 : timeSlot.until
                }
            });
            DataLayer.pushEvent(dataLayerEvent);
            monetate().trackEvent('BookEyeExam');
        }
    // Only run on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (isClient && !appointment) {
        goTo(whereStep, {});
        return null;
    }
    return /*#__PURE__*/ React.createElement(AppointmentConfirmationContainer, null);
};
ConfirmationRoot.getServerSideProps = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apolloClient, params, locale) {
        var ref;
        const formattedParams = getFormattedParams(params);
        if (formattedParams.globalStoreId === undefined) {
            throw MissingStoreIdError();
        }
        if ((ref = formattedParams.alternativeUrlStores) === null || ref === void 0 ? void 0 : ref.includes(formattedParams.globalStoreId)) {
            throw AlternativeUrlStoreError();
        }
        if (formattedParams.typeId === undefined) {
            throw MissingTypeIdError();
        }
        if (formattedParams.start === undefined) {
            throw MissingTimeSlotStartError();
        }
        if (formattedParams.end === undefined) {
            throw MissingTimeSlotEndError();
        }
        if (formattedParams.appointmentId === undefined) {
            throw MissingAppointmentIdError();
        }
        let promises = [
            null,
            null,
            undefined
        ];
        if (hasValidVirtualAppointmentParams(formattedParams)) {
            const type = yield getBookAppointmentType(apolloClient, formattedParams.typeId, locale);
            promises = [
                type ? generateVirtualAppointmentStoreByIdData(formattedParams.virtualStoreCode, type) : null,
                type,
                undefined, 
            ];
        } else if (hasInvalidVirtualAppointmentParams(formattedParams)) {
            throw InvalidVirtualAppointmentError();
        } else {
            promises = [
                getBookAppointmentStore(apolloClient, formattedParams.globalStoreId, locale, formattedParams.tryInStoreFlow, formattedParams.groupId),
                getBookAppointmentType(apolloClient, formattedParams.typeId, locale),
                formattedParams.tryInStoreProductSkus ? getBookAppointmentTryInStoreProducts(apolloClient, formattedParams.tryInStoreProductSkus, locale) : undefined, 
            ];
        }
        const [store, type, tryInStoreProducts] = yield Promise.all(promises);
        if (!store) {
            throw StoreNotFoundError();
        } else if (!type) {
            throw TypeNotFoundError();
        } else if (type && !isAppointmentTypeValid({
            typeId: type === null || type === void 0 ? void 0 : type.id,
            store
        })) {
            throw TypeNotAvailableForStoreError();
        }
        return Promise.resolve({
            store,
            type,
            timeSlot: {
                from: formattedParams.start,
                until: formattedParams.end
            },
            tryInStoreProducts
        });
    });
    return function(apolloClient, params, locale) {
        return _ref.apply(this, arguments);
    };
}();

export { ConfirmationRoot as default };
//# sourceMappingURL=ConfirmationRoot.js.map
